import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, FileText, Loader2, Pencil, Search, Upload, X } from 'lucide-react';

import {
  type Competence,
  type ReferenceCompetence,
  memoireService,
} from '../../services/api/memoire';
import { SORTIE_AGENT } from '../../hooks/useAgents';

interface Props {
  estAdmin: boolean;
  notifier: { showError: (t: string, m: string) => void; showInfo: (t: string, m: string) => void };
}

const champ = 'w-full rounded-xl border border-eg-bord bg-eg-blanc px-3 py-2 text-[13px] text-eg-encre placeholder:text-eg-estompe focus:border-eg-accent focus:outline-none focus:ring-4 focus:ring-eg-accent/10';

/**
 * Les compétences du programme : les skills importés, devenus assistants.
 *
 * On retrouve ici ce que chacun sait faire, quand il se déclenche, ce qu'il
 * lit, et ses instructions. Un skill se réimporte par-dessus l'ancien : c'est
 * le titre qui fait le lien.
 */
const Competences: React.FC<Props> = ({ estAdmin, notifier }) => {
  const [liste, setListe] = useState<Competence[] | null>(null);
  const [recherche, setRecherche] = useState('');
  const [ouverte, setOuverte] = useState<Competence | null>(null);
  const [references, setReferences] = useState<ReferenceCompetence[]>([]);
  const [instructionsVisibles, setInstructionsVisibles] = useState(false);
  const [edition, setEdition] = useState<{ summary: string; description: string; system_prompt: string } | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [import_, setImport] = useState(false);
  const champFichier = useRef<HTMLInputElement>(null);

  const charger = useCallback(() => memoireService.competences().then(setListe), []);
  useEffect(() => { charger().catch(() => setListe([])); }, [charger]);

  const ouvrir = async (competence: Competence) => {
    setEdition(null);
    setInstructionsVisibles(false);
    setOuverte(competence);
    setReferences([]);
    try {
      const [detail, docs] = await Promise.all([
        memoireService.competence(competence.id),
        memoireService.referencesCompetence(competence.id),
      ]);
      setOuverte(detail);
      setReferences(docs);
    } catch { /* la vignette suffit en attendant */ }
  };

  const filtrees = useMemo(() => {
    const cle = recherche.trim().toLowerCase();
    return (liste || []).filter((c) => !cle
      || c.title.toLowerCase().includes(cle)
      || (c.summary || '').toLowerCase().includes(cle)
      || (c.category_name || '').toLowerCase().includes(cle));
  }, [liste, recherche]);

  const parRubrique = useMemo(() => {
    const groupes: Record<string, Competence[]> = {};
    filtrees
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title))
      .forEach((c) => { (groupes[c.category_name || 'Autres'] = groupes[c.category_name || 'Autres'] || []).push(c); });
    return Object.entries(groupes);
  }, [filtrees]);

  const enregistrer = async (champs: Partial<Competence>) => {
    if (!ouverte) return;
    setOccupe(true);
    try {
      const misAJour = await memoireService.modifierCompetence(ouverte.id, champs);
      setOuverte({ ...ouverte, ...misAJour });
      setEdition(null);
      await charger();
    } catch (e: any) {
      notifier.showError('Enregistrement impossible', e.message);
    } finally {
      setOccupe(false);
    }
  };

  const importer = async (fichier: File) => {
    setImport(true);
    try {
      const importee = await memoireService.importerCompetence(fichier);
      notifier.showInfo('Compétence importée', `« ${importee.title} » est disponible dans OmnIA.`);
      await charger();
    } catch (e: any) {
      notifier.showError('Import impossible', e.message);
    } finally {
      setImport(false);
      if (champFichier.current) champFichier.current.value = '';
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-eg-estompe" />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher une compétence" className={`${champ} rounded-full pl-8`} />
        </div>
        {estAdmin && (
          <>
            <input
              ref={champFichier}
              type="file"
              accept=".skill,.zip,.md"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && importer(e.target.files[0])}
            />
            <button
              type="button"
              disabled={import_}
              onClick={() => champFichier.current?.click()}
              className="flex w-full items-center justify-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-2 text-[12.5px] font-medium text-white hover:bg-eg-profond disabled:opacity-50"
            >
              {import_ ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Importer un skill (.skill ou SKILL.md)
            </button>
          </>
        )}
        {liste === null ? (
          <div className="flex justify-center py-8 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {parRubrique.map(([rubrique, competences]) => (
              <div key={rubrique}>
                <div className="mb-1.5 px-1 text-[11.5px] font-medium text-eg-estompe">{rubrique}</div>
                <div className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                  {competences.map((c, index) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => ouvrir(c)}
                      className={`block w-full px-4 py-2.5 text-left hover:bg-eg-papier ${index > 0 ? 'border-t border-eg-bordure' : ''} ${ouverte?.id === c.id ? 'bg-eg-papier' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.is_active ? 'bg-eg-valide' : 'bg-eg-estompe'}`} />
                        <span className="truncate text-[13px] font-medium text-eg-encre">{c.title}</span>
                      </div>
                      <div className="pl-3.5 text-[11px] text-eg-estompe">
                        {SORTIE_AGENT[c.output_format] || c.output_format}{c.is_active ? '' : ' · désactivée'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="min-h-[420px] rounded-2xl bg-eg-blanc p-6 shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
        {!ouverte ? (
          <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-2 text-center text-eg-estompe">
            <FileText className="h-6 w-6" />
            <p className="max-w-[40ch] text-[13px]">
              Choisissez une compétence pour voir ce qu'elle sait faire, quand elle se déclenche et ce qu'elle lit.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap gap-1">
                  <span className="rounded-full bg-black/[0.045] px-2 py-[3px] text-[11px] font-medium text-eg-brun">{ouverte.category_name}</span>
                  <span className="rounded-full bg-black/[0.045] px-2 py-[3px] text-[11px] font-medium text-eg-brun">{SORTIE_AGENT[ouverte.output_format] || ouverte.output_format}</span>
                  {ouverte.chiffres_nationaux && <span className="rounded-full bg-eg-accent/[0.12] px-2 py-[3px] text-[11px] font-medium text-eg-accent">Lit les chiffres DHIS2</span>}
                  {!ouverte.cadrage_actif && <span className="rounded-full bg-black/[0.045] px-2 py-[3px] text-[11px] font-medium text-eg-brun">Sans questions préalables</span>}
                </div>
                <h3 className="mt-2.5 text-[19px] font-semibold tracking-tight text-eg-encre">{ouverte.title}</h3>
              </div>
              {estAdmin && (
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    disabled={occupe}
                    onClick={() => enregistrer({ is_active: !ouverte.is_active })}
                    className="rounded-full bg-black/[0.045] px-3 py-1.5 text-[12px] font-medium text-eg-brun hover:bg-black/[0.07] disabled:opacity-50"
                  >
                    {ouverte.is_active ? 'Désactiver' : 'Activer'}
                  </button>
                  {!edition && (
                    <button
                      type="button"
                      onClick={() => setEdition({
                        summary: ouverte.summary || '',
                        description: ouverte.description || '',
                        system_prompt: ouverte.system_prompt || '',
                      })}
                      className="inline-flex items-center gap-1 rounded-full bg-eg-ombre px-3 py-1.5 text-[12px] font-medium text-white hover:bg-eg-profond"
                    >
                      <Pencil className="h-3 w-3" /> Modifier
                    </button>
                  )}
                </div>
              )}
            </div>

            {edition ? (
              <div className="mt-5 space-y-3">
                <label className="block text-[12px] font-medium text-eg-gris">
                  Ce qu'elle fait, en une phrase (vignette)
                  <input value={edition.summary} onChange={(e) => setEdition({ ...edition, summary: e.target.value })} className={`${champ} mt-1`} />
                </label>
                <label className="block text-[12px] font-medium text-eg-gris">
                  Quand la déclencher (lu par l'aiguilleur)
                  <textarea value={edition.description} onChange={(e) => setEdition({ ...edition, description: e.target.value })} rows={5} className={`${champ} mt-1`} />
                </label>
                <label className="block text-[12px] font-medium text-eg-gris">
                  Instructions
                  <textarea value={edition.system_prompt} onChange={(e) => setEdition({ ...edition, system_prompt: e.target.value })} rows={16} className={`${champ} mt-1 font-mono text-[12px] leading-relaxed`} />
                </label>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setEdition(null)} className="inline-flex items-center gap-1 rounded-full bg-black/[0.045] px-3.5 py-1.5 text-[12.5px] font-medium text-eg-brun hover:bg-black/[0.07]">
                    <X className="h-3.5 w-3.5" /> Annuler
                  </button>
                  <button type="button" disabled={occupe} onClick={() => enregistrer(edition)} className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5 text-[12.5px] font-medium text-white hover:bg-eg-profond disabled:opacity-50">
                    {occupe ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Enregistrer
                  </button>
                </div>
              </div>
            ) : (
              <>
                {ouverte.summary && <p className="mt-3 text-[14px] leading-relaxed text-eg-brun">{ouverte.summary}</p>}

                <h4 className="mt-6 text-[12.5px] font-medium text-eg-encre">Quand elle se déclenche</h4>
                <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-eg-gris">{ouverte.description || '—'}</p>

                <h4 className="mt-6 text-[12.5px] font-medium text-eg-encre">Ce qu'elle lit ({references.length})</h4>
                {references.length === 0 ? (
                  <p className="mt-1.5 text-[13px] text-eg-estompe">Aucun document de référence propre.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {references.map((r) => (
                      <li key={r.id} className="flex items-center gap-2 text-[12.5px] text-eg-brun">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-eg-estompe" />
                        <span className="truncate">{r.name}</span>
                        <span className="ml-auto shrink-0 font-mono text-[10.5px] text-eg-estompe">
                          {Math.round(r.caracteres / 1000)} k car.
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {ouverte.system_prompt !== undefined && (
                  <>
                    <button
                      type="button"
                      onClick={() => setInstructionsVisibles((v) => !v)}
                      className="mt-6 inline-flex items-center gap-1 text-[12.5px] font-medium text-eg-encre"
                    >
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${instructionsVisibles ? '' : '-rotate-90'}`} />
                      Instructions ({Math.round((ouverte.system_prompt || '').length / 1000)} k caractères)
                    </button>
                    {instructionsVisibles && (
                      <pre className="mt-2 max-h-[480px] overflow-y-auto whitespace-pre-wrap rounded-xl bg-eg-papier px-4 py-3 font-mono text-[11.5px] leading-relaxed text-eg-brun">
                        {ouverte.system_prompt}
                      </pre>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Competences;
